import axios from "axios";
import moment from "moment";

import { config } from "../../config";

import { EVCharger, EVChargerType, EVChargerOutputType, EVChargerOutputPortType, EVChargerStatus, EVChargerOutputStatus, EVChargerOutputPortStatus, EVChargerInstallationStatus } from '@/entity/station/evcharger';
import { EVChargerTransformer } from '../transformers/evcharger';
import { LocalStorage } from '@/data/localstorage';
import { QueryInfo } from '@/util';
import { User } from '@/entity/authentication/user';
import { ValueRange } from '@/entity/value-range';
import { EVChargerCommunicationProtocol } from '@/entity/station/evcharger-communication-protocol';
import { EVChargerTypeTransformer } from '../transformers/evcharger-type';
import { EVChargeTariff } from '@/entity/station/evcharge-tariff';
import { Result } from '..';

export interface ChargerQuery {
    id?: string
    serialNo?: string
    chargerType?: EVChargerType
    manufacturer?: string
    effectiveBetween?: {
        from?: Date,
        to?: Date
    }
    installationStatus: EVChargerInstallationStatus
    status?: EVChargerStatus
}

export class EVChargerService {
    async chargersAtPageByPaginatedWithSizeMatchingQuery(page: number, size: number, query?: ChargerQuery): Promise<Result<EVCharger>> {        
        let queryInfo: any = {                     
            page: page,
            "page-size": size,            
        }
        if(query) {
            if(query.id) {
                queryInfo = { ...queryInfo,
                    chargerId: query.id
                }
            }
            if(query.serialNo) {
                queryInfo = { ...queryInfo,
                    serialNo: query.serialNo
                }
            }
            if(query.chargerType) {
                queryInfo = { ...queryInfo,
                    chargerType: {
                        code: query.chargerType.id
                    }
                }
            }
            if(query.manufacturer) {
                queryInfo = { ...queryInfo,
                    manufacturer: query.manufacturer
                }
            }
            if(query.effectiveBetween) {
                if(query.effectiveBetween.from) {
                    queryInfo = { ...queryInfo,
                        effectionFrom: moment(query.effectiveBetween.from).utc().toISOString()
                    }                    
                }
                if(query.effectiveBetween.to) {
                    queryInfo = { ...queryInfo,
                        effectionTo: moment(query.effectiveBetween.to).utc().toISOString()
                    }                    
                }
            }
            if(query.installationStatus) {
                queryInfo = { ...queryInfo,
                    installationStatus: {
                        code: query.installationStatus.id
                    }
                }
            }
            if(query.status) {
                queryInfo = { ...queryInfo,
                    status: {
                        code: query.status.id
                    }
                }
            }
        }
        let response = await axios.get(config.api.host + "/chargers/search" + new QueryInfo(queryInfo).toURLString(), {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let chargersInfos: any[] = response.data.data
        let chargers: EVCharger[] = []
        chargersInfos.forEach(chargerInfo => {
            chargers.push(new EVChargerTransformer().transform(chargerInfo))
        });
        return Promise.resolve(new Result<EVCharger>(response.data.meta.totalrecords, chargers))
    }

    async chargerWithId(id: string): Promise<EVCharger> {
        let response = await axios.get(config.api.host + "/chargers/" + id, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let chargerInfo: any = response.data.data        
        let charger = new EVChargerTransformer().transform(chargerInfo)
        return Promise.resolve(charger)
    }

    async createChargerByAuthor(charger: EVCharger, author: User): Promise<void> {
        let chargerInfo = new EVChargerTransformer().deTransform(charger)
        chargerInfo = {
            userId: author.id,
            ...chargerInfo        
        }
        
        await axios.post(config.api.host + "/chargers", {
            data: chargerInfo
        }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        return Promise.resolve()
    }

    async updateChargerByAuthor(charger: EVCharger, author: User): Promise<void> {
        let chargerInfo = new EVChargerTransformer().deTransform(charger)
        chargerInfo = {
            userId: author.id,
            ...chargerInfo        
        }
        await axios.put(config.api.host + "/chargers/" + charger.id, {
            data: chargerInfo
        }, {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        return Promise.resolve()
    }

    async chargerTypes(): Promise<EVChargerType[]> {
        let user = new LocalStorage().user
        let chargerTypes: EVChargerType[] = []
        if (user) {
            let response = await axios.get(config.api.host + "/chargers/config", {
                headers: {
                    Authorization: new LocalStorage().authToken
                }
            })
            let chargerTypesInfos: any[] = response.data.data
            chargerTypesInfos.forEach(chargerTypeInfo => {
                chargerTypes.push(new EVChargerTypeTransformer().transform(chargerTypeInfo))
            });            
        }
        return Promise.resolve(chargerTypes)        
    }

    async chargerTypesForReservation(): Promise<EVChargerType[]> {
        let user = new LocalStorage().user
        let chargerTypes: EVChargerType[] = []
        if (user) {
            let response = await axios.get(config.api.host + "/accounts/"+ user.id + "/reservations/chargertypes", {
                headers: {
                    Authorization: new LocalStorage().authToken
                }
            })
            let chargerTypesInfos: any[] = response.data.data
            chargerTypesInfos.forEach(chargerTypeInfo => {
                chargerTypes.push(new EVChargerTypeTransformer().transform(chargerTypeInfo))
            });        
        }
        return Promise.resolve(chargerTypes)
    }

    async chargerStatuses(): Promise<EVChargerStatus[]> {
        let response = await axios.get(config.api.host + "/chargers/status", {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let statusesInfos: any[] = response.data.data
        let statuses: EVChargerStatus[] = []
        statusesInfos.forEach(statusInfo => {
            statuses.push(new EVChargerStatus(
                statusInfo.code,
                statusInfo.description,
                "",
                ""

            ))
        });
        return Promise.resolve(statuses)
    }

    async chargerOutputStatuses(): Promise<EVChargerOutputStatus[]> {
        let response = await axios.get(config.api.host + "/chargers/output/status", {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let statusesInfos: any[] = response.data.data
        let statuses: EVChargerOutputStatus[] = []
        statusesInfos.forEach(statusInfo => {
            statuses.push(new EVChargerOutputStatus(
                statusInfo.code,
                statusInfo.description
            ))
        });
        return Promise.resolve(statuses)
    }

    async chargerOutputPortStasuses(): Promise<EVChargerOutputPortStatus[]> {
        let response = await axios.get(config.api.host + "/chargers/port/status", {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let statusesInfos: any[] = response.data.data
        let statuses: EVChargerOutputPortStatus[] = []
        statusesInfos.forEach(statusInfo => {
            statuses.push(new EVChargerOutputPortStatus(
                statusInfo.code,
                statusInfo.description
            ))
        });
        return Promise.resolve(statuses)
    }

    async chargerCommunicationProtocols(): Promise<EVChargerCommunicationProtocol[]> {
        let response = await axios.get(config.api.host + "/chargers/communications", {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let protocolsInfos: any[] = response.data.data
        let protocols: EVChargerCommunicationProtocol[] = []
        protocolsInfos.forEach(protocolInfo => {
            protocols.push(new EVChargerCommunicationProtocol(
                protocolInfo.code,
                protocolInfo.description
            ))
        });
        return Promise.resolve(protocols)
    }

    async chargerTariffs(): Promise<EVChargeTariff[]> {
        let response = await axios.get(config.api.host + "/tariffs", {
            headers: {
                Authorization: new LocalStorage().authToken
            }
        })
        let tariffsInfos: any[] = response.data.data
        let tariffs: EVChargeTariff[] = []
        tariffsInfos.forEach(tariffInfo => {
            tariffs.push(new EVChargeTariff(
                tariffInfo.code,
                tariffInfo.description
            ))
        });
        return Promise.resolve(tariffs)
    }
    

}